import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import ReactDOM from 'react-dom';
import ContextStore from '../../hooks/ContextStore';
import { IProductGroupResponse } from '../../interfaces/responses/ProductGroupResponse.interface';
import { IProductResponse } from '../../interfaces/responses/ProductResponse.interface';
import { IDeleteResponse } from '../../interfaces/responses/DeleteResponse.interface';
import { IErrorResponse } from '../../interfaces/responses/ErrorResponse.interface';
import { dispatch } from '../../dispatch/dispatch';
import { actions } from '../../dispatch';

interface IAddAllToGroupProps {
  setShowModal: Dispatch<SetStateAction<boolean>>;
  products: Array<IProductResponse>;
}

// ── All business logic is 100% unchanged ──────────────────────────────────
export const AddAllToGroupModal: FC<IAddAllToGroupProps> = ({ setShowModal, products }) => {
  const ref = useRef<HTMLFormElement>(null);
  const { contextStore, setContextStore } = useContext(ContextStore);
  const [searchString, setSearchString] = useState<string>('');
  const [productGroups, setProductGroups] = useState<Array<IProductGroupResponse>>(contextStore.productGroups);
  const [showForm, setShowForm] = useState<boolean>(true);
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const [selectedGroup, setSelectedGroup] = useState<IProductGroupResponse>({} as IProductGroupResponse);

  const onClickCancel = (e: React.SyntheticEvent) => { e.preventDefault(); setShowModal(false); };

  const onClickSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (selectedGroup._id) {
      contextStore.setShowSpinner(true);
      const addGroupsResponse: IDeleteResponse | IErrorResponse = await dispatch(
        actions.addProducts,
        { productIds: products.map((p) => p._id) },
        contextStore.token,
        { id: selectedGroup._id }
      );
      if ('error' in addGroupsResponse) { contextStore.setShowSpinner(false); return console.log(addGroupsResponse.error.message); }
      const productGroupProductsResponse: Array<IProductResponse> | IErrorResponse = await dispatch(
        actions.getProductGroupProducts,
        { fromDate: contextStore.date.fromDate, toDate: contextStore.date.toDate },
        contextStore.token,
        { productGroupId: selectedGroup._id }
      );
      if ('error' in productGroupProductsResponse) { contextStore.setShowSpinner(false); return console.log(productGroupProductsResponse.error.message); }
      const vProducts = contextStore.products;
      productGroupProductsResponse.map((product) => {
        const idx = vProducts.findIndex((vp) => vp._id === product._id);
        if (idx !== -1) vProducts[idx] = product;
      });
      contextStore.productGroupTrigger(contextStore.productGroups, vProducts);
      contextStore.overviewTrigger(vProducts);
      contextStore.productTrigger(vProducts);
      contextStore.setShowSpinner(false);
      setContextStore((past: any) => ({ ...past, products: vProducts }));
    }
    setShowModal(false);
  };

  useEffect(() => {
    const checkIfClickedOutside = (e: any) => {
      if (showForm && ref.current && !ref.current.contains(e.target)) {
        setShowForm(false);
        setShowModal(false);
      }
    };
    document.addEventListener('mousedown', checkIfClickedOutside);
    return () => document.removeEventListener('mousedown', checkIfClickedOutside);
  }, [showForm]);

  const onChangeSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductGroups(contextStore.productGroups.filter((pg) =>
      pg.name.toLowerCase().includes(e.target.value.toLowerCase())
    ));
    setSearchString(e.target.value);
  };

  // ── JSX redesigned with Tailwind ──────────────────────────────────────────
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <form ref={ref} onSubmit={onClickSubmit}
        className="w-full max-w-2xl rounded-2xl border border-border bg-card shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-base font-bold text-foreground">Add to Group</h2>
          {products.length > 0 && (
            <p className="mt-0.5 text-xs text-primary font-medium">
              {products.length} SKU{products.length > 1 ? 's' : ''} selected
            </p>
          )}
        </div>
        <div className="px-6 py-4">
          <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Select Group
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setOpenDropdown(!openDropdown)}
              className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2.5 text-sm text-foreground hover:border-muted-foreground transition-colors"
            >
              <span className="truncate">{selectedGroup.name || 'Select a group...'}</span>
              <span className="shrink-0 text-muted-foreground">{openDropdown ? '▲' : '▼'}</span>
            </button>
            {openDropdown && (
              <div className="absolute left-0 top-full z-10 mt-1 w-full rounded-xl border border-border bg-card shadow-xl overflow-hidden">
                <div className="p-2">
                  <input
                    type="text"
                    placeholder="Search groups..."
                    value={searchString}
                    onChange={onChangeSearchString}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div className="max-h-[420px] overflow-y-auto p-1">
                  {productGroups.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-muted-foreground">No groups found</div>
                  ) : (
                    productGroups.map((pg) => (
                      <button
                        key={pg._id}
                        type="button"
                        onClick={() => { setSelectedGroup(pg); setOpenDropdown(false); }}
                        className={`w-full text-left rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                          selectedGroup._id === pg._id
                            ? 'bg-primary text-white'
                            : 'text-foreground hover:bg-accent'
                        }`}
                      >
                        {pg.name}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-border bg-muted/30">
          <button type="button" onClick={onClickCancel}
            className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent transition-colors">
            Cancel
          </button>
          <button type="submit" disabled={!selectedGroup.name}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-40 transition-colors">
            {selectedGroup.name ? `Add to "${selectedGroup.name}"` : 'Add to Group'}
          </button>
        </div>
      </form>
    </div>,
    document.getElementById('addToGroup')!
  );
};
